import React from 'react';
import {
    ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
} from 'recharts';
import { Spinner } from '@material-tailwind/react';
import { useNavigate } from 'react-router-dom';
import {
    TicketIcon, ClockIcon, AlarmClockIcon, ShieldCheckIcon, TimerResetIcon,
    ArrowRightIcon, ArrowUpIcon, ArrowDownIcon, MinusIcon, RefreshCwIcon,
} from 'lucide-react';
import { useGetSupportDashboard } from '../../custom-hooks/use-support';
import { useGetSupportCategories } from '../../custom-hooks/use-support-categories';
import SupportTrendPanel from './SupportTrendPanel';
import { convertToPresentableText } from '../../utils/functions';
import { URLS } from '../../router-x/routes';

// Traffic-light bands from the client spec. Kept in one place so the thresholds are
// changed once rather than hunted through JSX.
const TONE = { good: 'good', warn: 'warn', bad: 'bad', neutral: 'neutral' };

const toneFor = {
    over48h: (v) => (v <= 2 ? TONE.good : v <= 5 ? TONE.warn : TONE.bad),
    over7d: (v) => (v === 0 ? TONE.good : v === 1 ? TONE.warn : TONE.bad),
    sla: (v) => (v === null ? TONE.neutral : v >= 90 ? TONE.good : v >= 80 ? TONE.warn : TONE.bad),
    resolve: (v) => (v === null ? TONE.neutral : v <= 2 ? TONE.good : v <= 3 ? TONE.warn : TONE.bad),
};

const TONE_CLASSES = {
    good: {
        chip: 'bg-green-50 text-green-700',
        dot: 'bg-green-500',
        label: 'Healthy',
        card: 'border-inv-cream-200',
    },
    warn: {
        chip: 'bg-amber-50 text-amber-700',
        dot: 'bg-amber-500',
        label: 'Needs attention',
        // Tinted so a card that needs looking at is visible without reading it —
        // the spec's whole premise is "drill down only when something is red".
        card: 'border-amber-200 bg-amber-50/40',
    },
    bad: {
        chip: 'bg-red-50 text-red-700',
        dot: 'bg-red-500',
        label: 'At risk',
        card: 'border-red-200 bg-red-50/40',
    },
    neutral: {
        chip: 'bg-inv-cream-100 text-inv-muted-dark',
        dot: 'bg-inv-muted',
        label: 'No data',
        card: 'border-inv-cream-200',
    },
};

const LEGEND = [
    { tone: TONE.good, label: 'Healthy' },
    { tone: TONE.warn, label: 'Needs attention' },
    { tone: TONE.bad, label: 'At risk' },
];

// Priority is ordinal, not categorical, so it gets a severity ramp rather than the
// categorical palette — worst is the same status red Inventory reserves for
// at/over-limit, easing down to a neutral grey for Low.
const PRIORITY_COLORS = {
    urgent: '#d03b3b',
    high: '#C2571A',
    medium: '#eda100',
    low: '#898781',
};

// Brand ramp, darkest first. A categorical palette would be wrong here: the bars are
// already labelled on the axis, so hue encodes nothing and just adds noise. Data is
// sorted by count, so the ramp doubles as a magnitude cue — and being a single hue
// varying only in lightness, it's inherently colour-blind safe.
const CATEGORY_COLORS = ['#9F440B', '#C2571A', '#D4753F', '#E09763', '#EBB98D', '#F3D2B4'];
const CATEGORY_FALLBACK = '#898781';

// Recharts needs a fixed number for a category axis, so derive it from the longest
// label actually present rather than reserving a worst-case gutter. A hardcoded 170
// left ~160px of dead space whenever the only label was something short like
// "Unassigned" — roughly half the chart on a phone.
const axisWidthFor = (rows, max = 160) => {
    const longest = rows.reduce((m, r) => Math.max(m, String(r.name || '').length), 0);
    return Math.min(max, Math.max(64, Math.round(longest * 6.4)));
};

// Chart chrome, matching inventory/DashboardView.js exactly.
const AXIS_TICK = '#898781';
const GRID_STROKE = '#e1e0d9';
const CURSOR_FILL = '#F1E9DC';

const tooltipStyle = {
    borderRadius: 12,
    border: '1px solid #E6DDCF',
    fontSize: 12,
    boxShadow: '0 12px 24px -12px rgba(40,22,8,0.25)',
};

// `showStatus` is false for Open Tickets — the spec gives it no green/yellow/red bands,
// since a raw count of open work isn't good or bad on its own.
const KpiCard = ({ icon: Icon, label, value, suffix = '', tone = TONE.neutral, hint, onClick, showStatus = true }) => {
    const classes = TONE_CLASSES[tone];
    return (
        <div
            onClick={onClick}
            className={`flex flex-col rounded-2xl border p-4 transition-shadow ${showStatus ? classes.card : 'border-inv-cream-200'} ${showStatus && tone !== TONE.neutral ? '' : 'bg-white'} ${onClick ? 'cursor-pointer hover:shadow-[0_14px_30px_-20px_rgba(40,22,8,0.45)]' : ''}`}
        >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-inv-brand-light text-inv-brand">
                <Icon size={18} />
            </div>
            <div className="mt-3 text-2xl font-extrabold leading-none text-inv-ink">
                {value === null || value === undefined ? '—' : `${value}${suffix}`}
            </div>
            <div className="mt-1 text-xs font-medium text-inv-muted">{label}</div>
            {hint && <div className="mt-1 text-[11px] text-inv-muted-light">{hint}</div>}
            {showStatus && (
                <div className={`mt-3 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${classes.chip}`}>
                    <span className={`h-2 w-2 rounded-full ${classes.dot}`} />
                    {classes.label}
                </div>
            )}
        </div>
    );
};

const Panel = ({ title, subtitle, children, empty, emptyText = 'Nothing to show yet', height }) => (
    <div className="rounded-2xl border border-inv-cream-200 bg-white p-4 sm:p-5">
        <div className="mb-4">
            <h3 className="font-bold text-inv-ink">{title}</h3>
            {subtitle && <p className="text-xs text-inv-muted">{subtitle}</p>}
        </div>
        {empty ? (
            <div className="flex items-center justify-center text-sm text-inv-muted-light" style={{ height: height || 200 }}>
                {emptyText}
            </div>
        ) : (
            height ? <div className="w-full" style={{ height }}>{children}</div> : children
        )}
    </div>
);

const TrendRow = ({ label, current, previous, invert = false }) => {
    const delta = current - previous;
    // "Closed" going up is good; "Created" and "Reopened" going up are not.
    const better = invert ? delta < 0 : delta > 0;
    const Icon = delta === 0 ? MinusIcon : delta > 0 ? ArrowUpIcon : ArrowDownIcon;
    const colour = delta === 0 ? 'text-inv-muted-light' : better ? 'text-green-600' : 'text-red-600';

    return (
        <div className="flex items-center justify-between border-b border-inv-cream-100 py-2.5 last:border-0">
            <span className="text-sm text-inv-muted-dark">{label}</span>
            <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-inv-ink">{current}</span>
                <span className={`flex w-16 items-center justify-end gap-1 text-xs font-medium ${colour}`}>
                    <Icon size={12} />
                    {delta === 0 ? 'same' : `${Math.abs(delta)}`}
                </span>
                <span className="w-14 text-right text-xs text-inv-muted-light">was {previous}</span>
            </div>
        </div>
    );
};

const SupportDashboardView = () => {
    const navigate = useNavigate();
    const { data, isLoading, isError, isFetching } = useGetSupportDashboard();
    const { data: categories = [] } = useGetSupportCategories({ includeInactive: true });

    if (isLoading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <Spinner className="h-8 w-8 text-inv-brand" />
            </div>
        );
    }

    if (isError || !data) {
        return <div className="py-10 text-center text-inv-muted">Failed to load the support dashboard.</div>;
    }

    const { kpis, byCategory, byPriority, byFloor, agingTickets, weekly } = data;

    // Resolve slugs through the configured taxonomy so a renamed category shows its
    // current name. includeInactive, because a retired category can still hold open
    // tickets and would otherwise render as a bare slug.
    const categoryNames = Object.fromEntries(categories.map((c) => [c.slug, c.name]));
    const categoryData = (byCategory || []).map((row) => ({
        name: categoryNames[row.category] || convertToPresentableText(row.category),
        value: row.count,
    }));
    const priorityData = (byPriority || []).map((row) => ({
        name: convertToPresentableText(row.priority),
        key: row.priority,
        value: row.count,
    }));

    const floorData = (byFloor || []).map((row) => ({ name: row.label, value: row.count }));

    const openTicketsUrl = URLS.ADMIN.SUPPORT;

    return (
        <div className="space-y-5">
            {/* No refresh button or "last updated" line: the data reloads on every open
                and polls while the tab is visible, so both would only ever restate that.
                Home's own Refresh Data button sits directly above this. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-extrabold tracking-tight text-inv-ink">Support Health</h2>
                    {isFetching && <RefreshCwIcon size={14} className="animate-spin text-inv-muted-light" />}
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    {LEGEND.map((item) => (
                        <span key={item.tone} className="flex items-center gap-1.5 text-xs font-medium text-inv-muted">
                            <span className={`h-2.5 w-2.5 rounded-full ${TONE_CLASSES[item.tone].dot}`} />
                            {item.label}
                        </span>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <KpiCard
                    icon={TicketIcon}
                    label="Open Tickets"
                    value={kpis.openTickets}
                    showStatus={false}
                    onClick={() => navigate(openTicketsUrl)}
                />
                <KpiCard
                    icon={ClockIcon}
                    label="Open > 48 hours"
                    value={kpis.over48h}
                    tone={toneFor.over48h(kpis.over48h)}
                />
                <KpiCard
                    icon={AlarmClockIcon}
                    label="Open > 7 days"
                    value={kpis.over7d}
                    tone={toneFor.over7d(kpis.over7d)}
                />
                <KpiCard
                    icon={ShieldCheckIcon}
                    label="Resolved within SLA"
                    value={kpis.slaCompliancePct}
                    suffix="%"
                    tone={toneFor.sla(kpis.slaCompliancePct)}
                    hint={kpis.closedSampleSize > 0
                        ? `${kpis.closedSampleSize} closed in 30 days`
                        : 'No tickets closed yet'}
                />
                <KpiCard
                    icon={TimerResetIcon}
                    label="Avg time to resolve"
                    value={kpis.avgResolveDays}
                    suffix=" days"
                    tone={toneFor.resolve(kpis.avgResolveDays)}
                    hint={kpis.closedSampleSize > 0 ? 'Last 30 days' : 'No tickets closed yet'}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Panel
                    title="Open by category"
                    subtitle="Where the open work is sitting"
                    height={260}
                    empty={categoryData.length === 0}
                    emptyText="No open tickets"
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={categoryData} layout="vertical" margin={{ left: 8, right: 24 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID_STROKE} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: AXIS_TICK }} />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={axisWidthFor(categoryData, 130)}
                                tick={{ fontSize: 11, fill: AXIS_TICK }}
                            />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CURSOR_FILL }} />
                            <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={22}>
                                {/* Ramp index is clamped, not cycled — wrapping would put the
                                    darkest tone on the smallest bar and invert the ramp. */}
                                {categoryData.map((entry, index) => (
                                    <Cell
                                        key={entry.name}
                                        fill={CATEGORY_COLORS[Math.min(index, CATEGORY_COLORS.length - 1)]}
                                    />
                                ))}
                                <LabelList dataKey="value" position="right" style={{ fontSize: 11, fill: AXIS_TICK }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Panel>

                <Panel
                    title="Open by priority"
                    subtitle="How urgent the queue is"
                    height={260}
                    empty={priorityData.every((row) => row.value === 0)}
                    emptyText="No open tickets"
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={priorityData.filter((row) => row.value > 0)}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={55}
                                outerRadius={90}
                                paddingAngle={2}
                                label={({ name, value }) => `${name}: ${value}`}
                                labelLine={false}
                                style={{ fontSize: 11 }}
                            >
                                {priorityData.filter((row) => row.value > 0).map((entry) => (
                                    <Cell key={entry.key} fill={PRIORITY_COLORS[entry.key] || CATEGORY_FALLBACK} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={tooltipStyle} />
                        </PieChart>
                    </ResponsiveContainer>
                </Panel>
            </div>

            <Panel
                title="Open by floor"
                subtitle="Where the open work is, by location"
                height={Math.max(200, floorData.length * 34 + 40)}
                empty={floorData.length === 0}
                emptyText="No open tickets"
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={floorData} layout="vertical" margin={{ left: 8, right: 24 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID_STROKE} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: AXIS_TICK }} />
                        <YAxis type="category" dataKey="name" width={axisWidthFor(floorData, 170)} tick={{ fontSize: 11, fill: AXIS_TICK }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CURSOR_FILL }} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={22}>
                            {/* Same clamped brand ramp as the category chart. "Unassigned" is
                                greyed rather than ramped — it's a gap to fill, not a real location. */}
                            {floorData.map((entry, index) => (
                                <Cell
                                    key={entry.name}
                                    fill={entry.name === 'Unassigned'
                                        ? CATEGORY_FALLBACK
                                        : CATEGORY_COLORS[Math.min(index, CATEGORY_COLORS.length - 1)]}
                                />
                            ))}
                            <LabelList dataKey="value" position="right" style={{ fontSize: 11, fill: AXIS_TICK }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Panel>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <Panel
                        title="Aging tickets"
                        subtitle="Open more than 48 hours, oldest first — click to open"
                        empty={(agingTickets || []).length === 0}
                        emptyText="Nothing has been open longer than 48 hours"
                        height={(agingTickets || []).length === 0 ? 200 : undefined}
                    >
                        <div className="custom-scrollbar max-h-[320px] overflow-y-auto pr-1">
                            {(agingTickets || []).map((ticket) => (
                                <button
                                    key={ticket._id}
                                    onClick={() => navigate(`${URLS.ADMIN.SUPPORT}/${ticket._id}`)}
                                    className="flex w-full items-center justify-between gap-3 border-b border-inv-cream-100 py-2.5 text-left transition-colors last:border-0 hover:bg-inv-cream"
                                >
                                    <div className="flex min-w-0 items-center gap-3">
                                        <span
                                            className="h-2 w-2 shrink-0 rounded-full"
                                            style={{ background: PRIORITY_COLORS[ticket.priority] || CATEGORY_FALLBACK }}
                                        />
                                        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-inv-muted-light">
                                            #{ticket.shortId}
                                        </span>
                                        <span className="truncate text-sm text-inv-ink">{ticket.subject}</span>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-3">
                                        <span className="hidden text-xs text-inv-muted sm:inline">
                                            {ticket.assignedAgent || 'Unassigned'}
                                        </span>
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                            ticket.ageDays >= 7 ? TONE_CLASSES.bad.chip : TONE_CLASSES.warn.chip
                                        }`}>
                                            {ticket.ageDays} days
                                        </span>
                                        <ArrowRightIcon size={14} className="text-inv-muted-light" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Panel>
                </div>

                <Panel title="This week" subtitle="Compared with last week">
                    <TrendRow label="Created" current={weekly.thisWeek.created} previous={weekly.lastWeek.created} invert />
                    {/* Counts resolutionDate, which is stamped on entry to Resolved — there
                        is no closedAt field, so a true "Closed" figure isn't available. */}
                    <TrendRow label="Resolved" current={weekly.thisWeek.closed} previous={weekly.lastWeek.closed} />
                    <TrendRow label="Net" current={weekly.thisWeek.net} previous={weekly.lastWeek.net} invert />
                    <TrendRow label="Reopened" current={weekly.thisWeek.reopened} previous={weekly.lastWeek.reopened} invert />
                </Panel>
            </div>

            <SupportTrendPanel />
        </div>
    );
};

export default SupportDashboardView;
